import { useSyncExternalStore } from "react";
import { useCoarsePointer } from "./useCoarsePointer.js";
import { useMinWidth } from "./useBreakpoint.js";

// ---- the desktop PREVIEW of the touch picker (ART-266) ----------------------------------------------------------
//
// On a touch device `Picker` opens its options in a bottom sheet. A desktop browser has a FINE pointer, so resizing
// it to phone width (or Chrome's device toolbar without touch emulation) never shows that sheet — which made the
// mobile picker the one surface you could not look at without a phone. The preview flag lets a non-production lane
// force the sheet whenever the viewport is narrower than `lg`, the same threshold the shell swaps on, so a desktop
// window sized like a phone behaves like one. The app owns WHETHER it is on (a dev cookie / launch param); this
// package only holds the bit, because `Picker` lives here and must not import the app.

let preview = false;
const listeners = new Set<() => void>();

/** Turn the desktop preview of the touch picker on or off. App-owned; inert unless a non-production lane calls it. */
export function setPickerSheetPreview(on: boolean): void {
  if (preview === on) return;
  preview = on;
  for (const l of listeners) l();
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/**
 * True where `Picker` should open its options in a bottom sheet: any coarse-pointer device (the sheet replaces the
 * native wheel there), or — with the preview flag on — a window narrower than `lg`.
 */
export function usePickerSheet(): boolean {
  const coarse = useCoarsePointer();
  const forced = useSyncExternalStore(subscribe, () => preview, () => false);
  const wide = useMinWidth("lg");
  return coarse || (forced && !wide);
}
